import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "shape",
    "clipShape",
    "insetOuter",
    "insetInner",
    "glare",
    "glareSettings",
    "glareAmountValue",
    "glareAngleValue",
    "insetBorder",
    "icon",
    "iconNameField",
    "iconButton",
    "iconSearch",
    "iconSettings",
    "iconOpacityValue",
    "stars",
    "ribbons",
    "ribbonLeft",
    "ribbonRight",
    "previewSvg",
  ];

  static values = {
    shapePaths: Object,
    shapeIconOffsets: Object,
  };

  updateColor(event) {
    const color = event.target.value;
    const shape = this.shapeTarget;
    const stale = [...shape.classList].filter((c) => c.startsWith("fill-"));
    stale.forEach((c) => shape.classList.remove(c));
    shape.classList.add(`fill-${color}-500`);
    if (this.hasRibbonLeftTarget) {
      const ribbonStale = [...this.ribbonLeftTarget.classList].filter((c) => c.startsWith("fill-"));
      ribbonStale.forEach((c) => this.ribbonLeftTarget.classList.remove(c));
      this.ribbonLeftTarget.classList.add(`fill-${color}-700`);
    }
    if (this.hasRibbonRightTarget) {
      const ribbonStale = [...this.ribbonRightTarget.classList].filter((c) => c.startsWith("fill-"));
      ribbonStale.forEach((c) => this.ribbonRightTarget.classList.remove(c));
      this.ribbonRightTarget.classList.add(`fill-${color}-700`);
    }
  }

  updateShape(event) {
    const shape = event.target.value;
    const paths = this.shapePathsValue[shape];
    if (!paths) return;
    this.shapeTarget.setAttribute("d", paths.outer);
    this.clipShapeTarget.setAttribute("d", paths.outer);
    this.insetOuterTarget.setAttribute("d", paths.inset_outer);
    this.insetInnerTarget.setAttribute("d", paths.inset_inner);

    const offsets = this.shapeIconOffsetsValue[shape];
    if (offsets) {
      this.iconTarget.setAttribute("x", offsets.x);
      this.iconTarget.setAttribute("y", offsets.y);
      this.iconTarget.setAttribute("width", offsets.size);
      this.iconTarget.setAttribute("height", offsets.size);
    }

    if (this.hasRibbonsTarget) {
      this.ribbonsTarget.style.display = shape === "rosette" ? "" : "none";
    }

    if (this.hasPreviewSvgTarget) {
      if (shape === "rosette") {
        this.previewSvgTarget.setAttribute("viewBox", "0 0 100 125");
        this.previewSvgTarget.setAttribute("height", "240");
      } else {
        this.previewSvgTarget.setAttribute("viewBox", "0 0 100 100");
        this.previewSvgTarget.setAttribute("height", "192");
      }
    }
  }

  updateGlare(event) {
    const value = event.target.value;
    const glare = this.glareTarget;
    if (value === "none") {
      glare.style.display = "none";
      this.glareSettingsTarget.classList.add("hidden");
      return;
    }
    glare.style.display = "";
    glare.setAttribute("fill", value === "light" ? "white" : "black");
    this.glareSettingsTarget.classList.remove("hidden");
  }

  updateGlareAmount(event) {
    const amount = parseInt(event.target.value, 10);
    this.glareTarget.setAttribute("fill-opacity", amount / 100);
    if (this.hasGlareAmountValueTarget) {
      this.glareAmountValueTarget.textContent = `${amount}%`;
    }
  }

  updateGlareAngle(event) {
    const angle = parseInt(event.target.value, 10);
    this.glareTarget.setAttribute("transform", `rotate(${-angle} 50 50)`);
    if (this.hasGlareAngleValueTarget) {
      this.glareAngleValueTarget.textContent = `${angle}°`;
    }
  }

  updateInsetBorder(event) {
    this.insetBorderTarget.style.display = event.target.checked ? "" : "none";
  }

  selectIcon(event) {
    const button = event.currentTarget;
    const name = button.dataset.iconName;
    const sourceSvg = button.querySelector("svg");
    this.iconTarget.innerHTML = sourceSvg.innerHTML;
    this.iconNameFieldTarget.value = name;
    this.iconTarget.style.display = "";
    this.iconSettingsTarget.classList.remove("hidden");
    this._highlightSelectedIconButton(name);
  }

  clearIcon() {
    this.iconTarget.innerHTML = "";
    this.iconNameFieldTarget.value = "";
    this.iconTarget.style.display = "none";
    this.iconSettingsTarget.classList.add("hidden");
    this._highlightSelectedIconButton(null);
  }

  filterIcons(event) {
    const term = event.target.value.trim().toLowerCase();
    this.iconButtonTargets.forEach((button) => {
      const name = button.dataset.iconName || "";
      button.style.display = name.includes(term) ? "" : "none";
    });
  }

  updateIconColor(event) {
    const color = event.target.dataset.color;
    const icon = this.iconTarget;
    const stale = [...icon.classList].filter((c) => c.startsWith("text-"));
    stale.forEach((c) => icon.classList.remove(c));
    if (color === "white") {
      icon.classList.add("text-white");
    } else if (color === "black") {
      icon.classList.add("text-black");
    } else {
      icon.classList.add(`text-${color}-500`);
    }
  }

  updateIconOpacity(event) {
    const amount = parseInt(event.target.value, 10);
    this.iconTarget.setAttribute("opacity", amount / 100);
    if (this.hasIconOpacityValueTarget) {
      this.iconOpacityValueTarget.textContent = `${amount}%`;
    }
  }

  updateStars(event) {
    const count = parseInt(event.target.value, 10);
    const container = this.starsTarget;
    container.innerHTML = "";
    if (count === 0) {
      container.style.display = "none";
      return;
    }
    container.style.display = "";
    const size = 28;
    const points = this._starPoints(size);
    const offset = (size * 0.3).toFixed(1);
    for (let i = 0; i < count; i++) {
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      svg.setAttribute("width", size);
      svg.setAttribute("height", size);
      svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
      svg.setAttribute("role", "img");
      svg.setAttribute("aria-label", "star");
      svg.classList.add("fill-amber-500");
      if (count === 3 && i === 1) {
        svg.style.transform = `translateY(-${offset}px)`;
      }
      const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
      polygon.setAttribute("points", points);
      svg.appendChild(polygon);
      container.appendChild(svg);
    }
  }

  _starPoints(size) {
    const outerR = size / 2;
    const innerR = outerR * 0.382;
    const cx = size / 2;
    const cy = size / 2;
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const angle = ((i * 36) - 90) * Math.PI / 180;
      pts.push(`${(cx + r * Math.cos(angle)).toFixed(2)} ${(cy + r * Math.sin(angle)).toFixed(2)}`);
    }
    return pts.join(" ");
  }

  _highlightSelectedIconButton(name) {
    this.iconButtonTargets.forEach((button) => {
      if (button.dataset.iconName === name) {
        button.classList.add("bg-white", "ring-2", "ring-teal-500");
      } else {
        button.classList.remove("bg-white", "ring-2", "ring-teal-500");
      }
    });
  }
}
